import React, { useState, useEffect } from 'react';
import { getUiPref, saveUiPref } from '../../data/consultorioData';

export const EMPTY = 'Sin registrar';

export function hasValue(v) {
  if (v === undefined || v === null) return false;
  return String(v).trim() !== '';
}

export function displayValue(value) {
  if (value === undefined || value === null || value === '') return EMPTY;
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  return String(value);
}

export function InfoCard({ label, value, icon, highlight, fullWidth }) {
  const empty = !hasValue(value);
  return (
    <div
      className={`fo-info-card${empty ? ' fo-info-card--empty' : ''}${highlight ? ' fo-info-card--highlight' : ''}${fullWidth ? ' fo-info-card--full' : ''}`}
    >
      {icon ? (
        <span className="fo-info-card-icon" aria-hidden>
          <i className={`bi ${icon}`} />
        </span>
      ) : null}
      <div className="fo-info-card-body">
        <span className="fo-info-card-label">{label}</span>
        <span className="fo-info-card-value">{empty ? EMPTY : value}</span>
      </div>
    </div>
  );
}

export function InfoGrid({ children, columns }) {
  return (
    <div className={`fo-general-grid${columns === 2 ? ' fo-general-grid--2' : ''}`}>
      {children}
    </div>
  );
}

/** Bloque de texto largo (motivo, notas, alergias). */
export function NarrativeBlock({ label, value, icon, highlight, variant = 'default' }) {
  const text = displayValue(value);
  const empty = text === EMPTY;
  return (
    <div
      className={`fo-narrative fo-narrative--${variant}${empty ? ' fo-narrative--empty' : ''}${highlight ? ' fo-narrative--highlight' : ''}`}
    >
      <div className="fo-narrative-head">
        {icon ? <i className={`bi ${icon}`} aria-hidden /> : null}
        <span className="fo-narrative-label">{label}</span>
      </div>
      <p className="fo-narrative-text">{text}</p>
    </div>
  );
}

export function KeyValueTable({ title, pairs, icon }) {
  const rows = (pairs || []).filter((p) => hasValue(p.value));
  if (rows.length === 0) {
    return (
      <div className="fo-kv-block fo-kv-block--empty">
        {title ? (
          <h5 className="fo-kv-block-title">
            {icon ? <i className={`bi ${icon}`} /> : null}
            {title}
          </h5>
        ) : null}
        <p className="fo-kv-empty-msg">{EMPTY}</p>
      </div>
    );
  }
  return (
    <div className="fo-kv-block">
      {title ? (
        <h5 className="fo-kv-block-title">
          {icon ? <i className={`bi ${icon}`} /> : null}
          {title}
          <span className="fo-kv-block-count">{rows.length}</span>
        </h5>
      ) : null}
      <div className="fo-kv-table-wrap">
        <table className="fo-kv-table">
          <tbody>
            {rows.map((p) => (
              <tr key={p.k}>
                <th scope="row">{p.label}</th>
                <td>{String(p.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function StatusPill({ ok, labelOk = 'Completo', labelNo = 'Pendiente' }) {
  return (
    <span className={`fo-status-pill${ok ? ' fo-status-pill--ok' : ' fo-status-pill--pending'}`}>
      <i className={`bi ${ok ? 'bi-check-circle-fill' : 'bi-dash-circle'}`} />
      {ok ? labelOk : labelNo}
    </span>
  );
}

export function ClinicalSubsection({ title, icon, children }) {
  return (
    <div className="fo-clinical-sub">
      {title ? (
        <h5 className="fo-clinical-sub-title">
          {icon ? <i className={`bi ${icon}`} /> : null}
          {title}
        </h5>
      ) : null}
      <div className="fo-clinical-sub-body">{children}</div>
    </div>
  );
}

export function SectionEmpty({ icon = 'bi-inbox', message = 'No hay datos registrados en esta sección.', onComplete = null }) {
  return (
    <div className="fo-section-empty">
      <i className={`bi ${icon}`} aria-hidden />
      <p>{message}</p>
      {onComplete ? (
        <button type="button" className="cp-btn cp-btn--primary cp-btn--sm mt-2" onClick={onComplete}>
          <i className="bi bi-plus-circle" />
          Completar esta sección
        </button>
      ) : null}
    </div>
  );
}

export function SectionEditAction({ hasData, onEdit, labelComplete = 'Completar', labelEdit = 'Editar' }) {
  if (!onEdit) return null;
  return (
    <button
      type="button"
      className={`fo-section-edit-btn${hasData ? ' fo-section-edit-btn--edit' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        onEdit();
      }}
    >
      <i className={`bi ${hasData ? 'bi-pencil' : 'bi-plus-circle'}`} />
      {hasData ? labelEdit : labelComplete}
    </button>
  );
}

export function ClinicalSection({
  sectionId,
  title,
  subtitle,
  icon,
  accentColor = '#5D87FF',
  defaultOpen = false,
  badge,
  hasData = false,
  onEditSection = null,
  children,
}) {
  const storageKey = `fo_clinical_${sectionId}`;
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    let cancelled = false;
    getUiPref(storageKey).then((saved) => {
      if (!cancelled && saved !== null) setOpen(saved === 'true');
    });
    return () => { cancelled = true; };
  }, [storageKey]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    saveUiPref(storageKey, next);
  };

  return (
    <div
      className={`fo-clinical${open ? ' fo-clinical--open' : ''}`}
      style={{ '--fo-accent': accentColor }}
    >
      <div className="fo-clinical-hdr">
        <button
          type="button"
          className="fo-clinical-hdr-toggle"
          onClick={toggle}
          aria-expanded={open}
          aria-controls={`fo-clinical-body-${sectionId}`}
        >
          <span className="fo-clinical-icon">
            <i className={`bi ${icon}`} />
          </span>
          <span className="fo-clinical-titles">
            <span className="fo-clinical-title">{title}</span>
            {subtitle ? <span className="fo-clinical-sub">{subtitle}</span> : null}
          </span>
          {badge != null && badge !== '' ? <span className="fo-clinical-badge">{badge}</span> : null}
        </button>
        <button
          type="button"
          className="fo-clinical-expand-btn"
          onClick={toggle}
          aria-expanded={open}
          aria-label={open ? 'Contraer sección' : 'Expandir sección'}
        >
          <i className="bi bi-chevron-down fo-clinical-chevron" aria-hidden />
        </button>
        <SectionEditAction hasData={hasData} onEdit={onEditSection ? () => onEditSection(sectionId) : null} />
      </div>
      <div className="fo-clinical-body" id={`fo-clinical-body-${sectionId}`}>
        <div className="fo-clinical-inner">{children}</div>
      </div>
    </div>
  );
}

export function MedicamentosTable({ filas, legacy }) {
  const rows = (filas || []).filter((r) => !!(r?.nombre || r?.dosis || r?.frecuencia || r?.via));
  if (rows.length === 0 && !hasValue(legacy)) return null;
  if (rows.length === 0) {
    return <NarrativeBlock label="Medicamentos actuales" value={legacy} icon="bi-capsule" />;
  }
  return (
    <div className="fo-med-block">
      <h5 className="fo-kv-block-title">
        <i className="bi bi-capsule" />
        Medicamentos actuales
        <span className="fo-kv-block-count">{rows.length}</span>
      </h5>
      <div className="fo-emergencia-table-wrap">
        <table className="fo-emergencia-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Dosis</th>
              <th>Frecuencia</th>
              <th>Vía</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx}>
                <td>{r.nombre || EMPTY}</td>
                <td>{r.dosis || EMPTY}</td>
                <td>{r.frecuencia || EMPTY}</td>
                <td>{r.via || EMPTY}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function HosIndexChips({ placa, calculo, gingivitis }) {
  if (!hasValue(placa) && !hasValue(calculo) && !hasValue(gingivitis)) return null;
  return (
    <div className="fo-hos-chips">
      <span className="fo-hos-chips-label">Índices H.O.S.</span>
      {hasValue(placa) && (
        <span className="fo-hos-chip">
          <i className="bi bi-droplet" /> Placa {placa}
        </span>
      )}
      {hasValue(calculo) && (
        <span className="fo-hos-chip">
          <i className="bi bi-gem" /> Cálculo {calculo}
        </span>
      )}
      {hasValue(gingivitis) && (
        <span className="fo-hos-chip">
          <i className="bi bi-heart-pulse" /> Gingivitis {gingivitis}
        </span>
      )}
    </div>
  );
}
